/*
 * Benivo Paris winkelwagen (sections/benivo-cart.liquid), overgenomen van het eigen Zamucci-theme.
 * Werkt met de Shopify-winkelwagen (/cart.js, /cart/change.js). Kortingen worden door
 * Shopify berekend; dit paneel toont alleen hoe ver de klant van de volgende trede is.
 */

/**
 * Bepaalt de voortgang van de kortingsbalk. Pure functie.
 * @param {number} itemCount - Aantal producten in de winkelwagen
 * @param {{count: number, label: string}[]} tiers - Treden, oplopend op aantal
 * @returns {{next: {count: number, label: string} | null, missing: number, reached: {count: number, label: string} | null, segments: number[]}}
 *   segments: per trede hoeveel van het stuk balk ervoor gevuld is (0 tot 1)
 */
export function getRewardProgress(itemCount, tiers) {
  const sorted = [...tiers].filter((tier) => tier.count > 0).sort((a, b) => a.count - b.count);
  const count = Math.max(0, Math.floor(itemCount));
  const next = sorted.find((tier) => count < tier.count) ?? null;
  const reachedTiers = sorted.filter((tier) => count >= tier.count);
  const reached = reachedTiers.length ? reachedTiers[reachedTiers.length - 1] : null;

  const segments = sorted.map((tier, index) => {
    const start = index === 0 ? 0 : sorted[index - 1].count;
    const span = tier.count - start;
    if (span <= 0) return 1;
    return Math.min(1, Math.max(0, (count - start) / span));
  });

  return { next, missing: next ? next.count - count : 0, reached, segments };
}

/**
 * Zet een bedrag in centen om naar tekst volgens het geldformaat van de winkel.
 * Ondersteunt {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}}
 * en {{amount_no_decimals_with_comma_separator}}.
 * @param {number} cents
 * @param {string} format - bijv. "€{{amount_with_comma_separator}}"
 * @returns {string}
 */
export function formatMoney(cents, format) {
  const value = (Number(cents) || 0) / 100;
  const withDelimiters = (decimals, thousands, decimal) => {
    const [whole, fraction] = value.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_match, placeholder) => {
    switch (placeholder) {
      case 'amount_no_decimals':
        return withDelimiters(0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(0, '.', ',');
      case 'amount':
      default:
        return withDelimiters(2, ',', '.');
    }
  });
}

/**
 * Haalt de kortingen op winkelwagenniveau uit een Shopify-winkelwagen. /cart.js en
 * /cart/change.js geven deze in een andere vorm terug; beide worden ondersteund.
 * @param {any} cart
 * @returns {{title: string, amount: number}[]}
 */
export function getCartDiscounts(cart) {
  return (cart?.cart_level_discount_applications || [])
    .map((entry) => {
      const application = entry.discount_application || entry;
      const amount = Number(entry.amount ?? application.total_allocated_amount) || 0;
      return { title: String(application.title || '').trim(), amount };
    })
    .filter((discount) => discount.amount > 0);
}

/**
 * Zet seconden om naar mm:ss.
 * @param {number} seconds
 * @returns {string}
 */
export function formatCountdown(seconds) {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

const TIMER_STORAGE_KEY = 'benivo-cart-timer-end';
const TAG_ICON =
  '<svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>';
const TRASH_ICON =
  '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 13h12l1-13M9 7V4h6v3"/></svg>';

/** @param {string} text */
function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

class BenivoCart extends HTMLElement {
  /** @type {{tiers: {count: number, label: string}[], [key: string]: any}} */
  config = { tiers: [] };
  /** @type {Map<string, Promise<any>>} */
  #productCache = new Map();
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timerInterval;

  connectedCallback() {
    const data = this.querySelector('script[data-config]');
    this.config = data ? JSON.parse(data.textContent || '{}') : this.config;
    this.moneyFormat = this.getAttribute('data-money-format') || '{{amount}}';

    this.querySelectorAll('[data-close]').forEach((element) => element.addEventListener('click', () => this.close()));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    if (this.hasAttribute('data-enabled')) {
      document.body.dataset.benivoCart = 'true';
      // De cart-drawer van het theme zou bij "ajouter au panier" ook openen; die vervalt
      document.getElementById('cart-drawer')?.remove();
      // Winkelwagenknop in de header opent dit paneel
      window.addEventListener('click', this.#onCartIconClick, true);
      // Na "ajouter au panier" stuurt het theme cart:change met baseEvent variant:add
      document.addEventListener('cart:change', this.#onThemeCartChange);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.#onCartIconClick, true);
    document.removeEventListener('cart:change', this.#onThemeCartChange);
    clearInterval(this.#timerInterval);
  }

  /** @param {MouseEvent} event */
  #onCartIconClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const trigger = target?.closest?.('[aria-controls="cart-drawer"], a[href="/cart"]');
    if (!trigger || trigger.closest('benivo-cart')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open();
  };

  /** @param {Event} event */
  #onThemeCartChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.baseEvent === 'variant:add') this.open();
  };

  async open() {
    this.hidden = false;
    document.documentElement.classList.add('benivo-cart-open');
    // Eerst de begintoestand laten tekenen, dan openen (werkt ook zonder animatieframes)
    void this.offsetWidth;
    this.setAttribute('open', '');
    this.#startTimer();
    await this.refresh();
    this.querySelector('.benivo-cart__close')?.focus({ preventScroll: true });
  }

  close() {
    this.removeAttribute('open');
    document.documentElement.classList.remove('benivo-cart-open');
    clearInterval(this.#timerInterval);
    setTimeout(() => {
      if (!this.hasAttribute('open')) this.hidden = true;
    }, 300);
  }

  async refresh() {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    await this.#render(cart);
    this.#notifyTheme(cart);
  }

  /** @param {string} key @param {number} quantity */
  async changeQuantity(key, quantity) {
    this.classList.add('is-loading');
    try {
      const cart = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      }).then((response) => response.json());
      await this.#render(cart);
      this.#notifyTheme(cart);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  /**
   * Laat het theme (teller en stip bij het winkelwagenicoon) de nieuwe winkelwagen zien.
   * @param {any} cart
   */
  #notifyTheme(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'benivo-cart:change', cart } })
    );
  }

  /** @param {string} handle */
  #productData(handle) {
    if (!this.#productCache.has(handle)) {
      this.#productCache.set(
        handle,
        fetch(`/products/${handle}.js`)
          .then((response) => response.json())
          .catch(() => null)
      );
    }
    return this.#productCache.get(handle);
  }

  /** @param {any} cart */
  async #render(cart) {
    this.#renderRewards(cart.item_count);

    const list = this.querySelector('[data-items]');
    const empty = this.querySelector('[data-empty]');
    const footer = this.querySelector('[data-footer]');
    if (!list || !empty || !footer) return;

    if (!cart.items.length) {
      list.innerHTML = '';
      empty.textContent = this.config.emptyText;
      empty.hidden = false;
      footer.hidden = true;
      return;
    }
    empty.hidden = true;
    footer.hidden = false;

    const products = await Promise.all(cart.items.map((item) => this.#productData(item.handle)));
    list.innerHTML = cart.items.map((item, index) => this.#itemHtml(item, products[index])).join('');

    list.querySelectorAll('[data-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.getAttribute('data-key') || '';
        const quantity = Number(button.getAttribute('data-quantity-change'));
        this.changeQuantity(key, quantity);
      });
    });
    list.querySelectorAll('input[data-key]').forEach((input) => {
      input.addEventListener('change', () => {
        const element = /** @type {HTMLInputElement} */ (input);
        this.changeQuantity(element.dataset.key || '', Math.max(0, Number(element.value) || 0));
      });
    });

    const discounts = this.querySelector('[data-discounts]');
    if (discounts) {
      discounts.innerHTML = getCartDiscounts(cart)
        .map(
          (discount) => `
          <li class="benivo-cart__discount">
            <span>${escapeHtml(discount.title)}</span>
            <span>−${formatMoney(discount.amount, this.moneyFormat)}</span>
          </li>`
        )
        .join('');
    }

    const checkout = this.querySelector('[data-checkout]');
    if (checkout) {
      checkout.textContent = String(this.config.checkoutLabel || '{TOTAL}').replace(
        '{TOTAL}',
        formatMoney(cart.total_price, this.moneyFormat)
      );
    }
  }

  /** @param {any} item @param {any} product */
  #itemHtml(item, product) {
    const variant = product?.variants?.find((entry) => entry.id === item.variant_id);
    const compareAt = Number(variant?.compare_at_price) || 0;
    const unitPrice = item.final_price;
    const savingsPerUnit = compareAt > unitPrice ? compareAt - unitPrice : 0;
    const options = (item.options_with_values || [])
      .filter((option) => option.value !== 'Default Title')
      .map((option) => `${escapeHtml(option.name)}: ${escapeHtml(option.value)}`)
      .join(', ');
    const image = item.featured_image?.url || item.image;
    const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=176` : '';
    const key = escapeHtml(item.key);

    return `
      <li class="benivo-cart__item">
        <a href="${escapeHtml(item.url)}" class="benivo-cart__image">
          ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(item.product_title)}" width="88" height="88" loading="lazy">` : ''}
        </a>
        <div class="benivo-cart__details">
          <div class="benivo-cart__title-row">
            <a href="${escapeHtml(item.url)}" class="benivo-cart__title">${escapeHtml(item.product_title)}</a>
            <button type="button" class="benivo-cart__remove benivo-cart__button" data-key="${key}" data-quantity-change="0" aria-label="${escapeHtml(this.config.removeLabel || 'Supprimer')}">${TRASH_ICON}</button>
          </div>
          ${options ? `<p class="benivo-cart__options">${options}</p>` : ''}
          <p class="benivo-cart__prices">
            ${savingsPerUnit ? `<s class="benivo-cart__compare">${formatMoney(compareAt, this.moneyFormat)}</s>` : ''}
            <span class="benivo-cart__price">${formatMoney(unitPrice, this.moneyFormat)}</span>
            ${savingsPerUnit ? `<span class="benivo-cart__savings">${escapeHtml(String(this.config.savingsText || '').replace('{AMOUNT}', formatMoney(savingsPerUnit, this.moneyFormat)))}</span>` : ''}
          </p>
          <div class="benivo-cart__quantity">
            <button type="button" class="benivo-cart__button" data-key="${key}" data-quantity-change="${item.quantity - 1}" aria-label="${escapeHtml(this.config.decreaseLabel)}">−</button>
            <input type="number" min="0" inputmode="numeric" value="${item.quantity}" data-key="${key}" aria-label="${escapeHtml(this.config.quantityLabel || 'Quantité')}">
            <button type="button" class="benivo-cart__button" data-key="${key}" data-quantity-change="${item.quantity + 1}" aria-label="${escapeHtml(this.config.increaseLabel)}">+</button>
          </div>
        </div>
      </li>`;
  }

  /** @param {number} itemCount */
  #renderRewards(itemCount) {
    const container = this.querySelector('[data-rewards]');
    const tiers = this.config.tiers || [];
    if (!container || !tiers.length) return;

    const progress = getRewardProgress(itemCount, tiers);
    let message;
    if (progress.next) {
      const template = progress.missing === 1 ? this.config.textBeforeSingular : this.config.textBefore;
      message = String(template).replace('{COUNT}', String(progress.missing)).replace('{REWARD}', escapeHtml(progress.next.label));
    } else {
      message = String(this.config.textDone).replace('{REWARD}', escapeHtml(progress.reached?.label || ''));
    }

    const sorted = [...tiers].sort((a, b) => a.count - b.count);
    container.innerHTML = `
      <p class="benivo-cart__rewards-text">${message}</p>
      <div class="benivo-cart__rewards-bar">
        ${sorted
          .map(
            (tier, index) => `
          <div class="benivo-cart__rewards-step${itemCount >= tier.count ? ' is-reached' : ''}">
            <span class="benivo-cart__rewards-track"><span style="width:${Math.round(progress.segments[index] * 100)}%"></span></span>
            <span class="benivo-cart__rewards-icon">${TAG_ICON}</span>
            <span class="benivo-cart__rewards-label">${escapeHtml(tier.label)}</span>
          </div>`
          )
          .join('')}
      </div>`;
  }

  #startTimer() {
    const element = this.querySelector('[data-timer]');
    if (!element) return;
    const minutes = Number(this.getAttribute('data-timer-minutes')) || 15;

    const readEnd = () => {
      try {
        return Number(sessionStorage.getItem(TIMER_STORAGE_KEY)) || 0;
      } catch {
        return 0;
      }
    };
    const writeEnd = (value) => {
      try {
        sessionStorage.setItem(TIMER_STORAGE_KEY, String(value));
      } catch {
        // geen opslag beschikbaar: afteller loopt alleen deze pagina
      }
    };

    let end = readEnd();
    if (!end || end <= Date.now()) {
      end = Date.now() + minutes * 60 * 1000;
      writeEnd(end);
    }

    const tick = () => {
      let remaining = Math.round((end - Date.now()) / 1000);
      if (remaining <= 0) {
        end = Date.now() + minutes * 60 * 1000;
        writeEnd(end);
        remaining = minutes * 60;
      }
      element.textContent = String(this.config.timerText || '{TIMER}').replace('{TIMER}', formatCountdown(remaining));
    };
    tick();
    clearInterval(this.#timerInterval);
    this.#timerInterval = setInterval(tick, 1000);
  }
}

if (!customElements.get('benivo-cart')) {
  customElements.define('benivo-cart', BenivoCart);
}
